import React from "react";
import { useLocation } from "react-router";
import {
  Link,
  NavigationSidebar,
  Tree,
  TreeView,
  useSidebarCollapsed,
} from "@sscs/ui";
import {
  AI_CONFIGURATION_ROUTE,
  AUDIT_ROUTE,
  CLI_CAPACITY_ROUTE,
  LICENSING_ROUTE,
  MEMBERS_ROUTE,
  PERMISSION,
  POLICY_CONFIG_ROUTE,
  SESSION_CONFIGURATION_ROUTE,
  SETTINGS_ROUTE,
  USER_DIRECTORY_ROUTE,
} from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";

type NavItem = {
  id: string;
  label: string;
  shortLabel: string;
  to: string;
};

type NavGroup = {
  heading: string;
  shortHeading: string;
  items: NavItem[];
};

// Shared left-hand navigation for the organization Administration pages. It
// mirrors the destinations (and their permission gating) of the Organization
// header menu - clicking an item lands on the exact same route as before, now
// rendered inside the Administration shell.
const Navigation = (): React.JSX.Element => {
  const organization = useCurrentOrganization();
  const organizationBase = `/organization/${organization?.id ?? ""}`;
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useSidebarCollapsed("administration");

  const canViewMembers = useAllowedTo({
    permissions: [PERMISSION.VIEW_ALL_ORGANIZATION_MEMBERS],
  });
  const canManageSso = useAllowedTo({ permissions: [PERMISSION.SETUP_SSO] });
  const canConfigureSession = useAllowedTo({
    permissions: [PERMISSION.MODIFY_SESSION_CONFIGURATION],
  });
  const canViewAnalysisCapacity = useAllowedTo({
    permissions: [PERMISSION.VIEW_ORGANIZATION_MONTHLY_CAPACITY_AND_USAGE],
  });
  const canViewCliCapacity = useAllowedTo({
    permissions: [PERMISSION.VIEW_CLI_SITE_KEY_ALLOCATIONS],
  });
  const canConfigureAi = useAllowedTo({
    permissions: [PERMISSION.MODIFY_AI_CONFIGURATION],
  });
  const canViewAuditLog = useAllowedTo({
    permissions: [PERMISSION.VIEW_AUDIT],
  });

  const accessItems: NavItem[] = [];
  if (canViewMembers) {
    accessItems.push({
      id: "administration-members",
      label: "Members & groups",
      shortLabel: "Members",
      to: `${organizationBase}/${MEMBERS_ROUTE}`,
    });
  }
  if (canManageSso) {
    accessItems.push({
      id: "administration-single-sign-on",
      label: "Single sign-on",
      shortLabel: "SSO",
      to: `/${SETTINGS_ROUTE}/${USER_DIRECTORY_ROUTE}`,
    });
  }
  if (canConfigureSession) {
    accessItems.push({
      id: "administration-sessions",
      label: "Sessions",
      shortLabel: "Sessions",
      to: `${organizationBase}/${SESSION_CONFIGURATION_ROUTE}`,
    });
  }

  const usageItems: NavItem[] = [];
  if (canViewAnalysisCapacity) {
    usageItems.push({
      id: "administration-portal-capacity",
      label: "Portal capacity",
      shortLabel: "Portal",
      to: `${organizationBase}/${LICENSING_ROUTE}`,
    });
  }
  if (canViewCliCapacity) {
    usageItems.push({
      id: "administration-cli-capacity",
      label: "CLI capacity",
      shortLabel: "CLI",
      to: `${organizationBase}/${CLI_CAPACITY_ROUTE}`,
    });
  }

  const platformItems: NavItem[] = [];
  if (canConfigureAi) {
    platformItems.push({
      id: "administration-ai-settings",
      label: "AI settings",
      shortLabel: "AI",
      to: `${organizationBase}/${AI_CONFIGURATION_ROUTE}`,
    });
  }
  if (canViewAuditLog) {
    platformItems.push({
      id: "administration-audit-logs",
      label: "Audit logs",
      shortLabel: "Audit",
      to: `${organizationBase}/${AUDIT_ROUTE}`,
    });
  }

  const groups: NavGroup[] = [
    {
      heading: "Access & identity",
      shortHeading: "Access",
      items: accessItems,
    },
    {
      heading: "Usage & capacity",
      shortHeading: "Usage",
      items: usageItems,
    },
    {
      heading: "Security",
      shortHeading: "Security",
      items: [
        {
          id: "administration-policies",
          label: "Policies",
          shortLabel: "Policies",
          to: `${organizationBase}/${POLICY_CONFIG_ROUTE}`,
        },
      ],
    },
    {
      heading: "Platform",
      shortHeading: "Platform",
      items: platformItems,
    },
  ].filter((group) => group.items.length > 0);

  const trees: Tree[] = groups.map((group) => ({
    title: group.heading,
    shortTitle: group.shortHeading,
    nodes: group.items.map((item) => ({
      id: item.id,
      label: (
        <Link to={item.to} id={item.id}>
          {item.label}
        </Link>
      ),
      shortLabel: <Link to={item.to}>{item.shortLabel}</Link>,
    })),
  }));

  // Selection is route-driven (replacing NavLink's isActive): the deepest item
  // route that prefixes the current path owns the active state.
  const selected =
    groups
      .flatMap((group) => group.items)
      .filter(
        (item) =>
          location.pathname === item.to ||
          location.pathname.startsWith(`${item.to}/`),
      )
      .sort((first, second) => second.to.length - first.to.length)[0]?.id ??
    null;

  return (
    <NavigationSidebar
      aria-label="administration-navigation"
      storageKey="administration"
      title={canManageSso ? "Manage Organization" : "View Organization"}
      isCollapsed={isCollapsed}
      onCollapse={setIsCollapsed}
    >
      <TreeView
        aria-label="administration-navigation"
        selected={selected}
        trees={trees}
      />
    </NavigationSidebar>
  );
};

export default Navigation;
